// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — the three modes. A mode has to change how the game PLAYS, not just a number, and the
// test each of these had to pass is: does a strategy that wins here LOSE in the other two?
//
//   • croft — 5×5, twelve doublets into exactly twenty-four cells. Cells and doublets are 1:1, so a
//     perfect fill is real and attainable and legality starts binding around round 8. You draft
//     defensively: #14 Pine|Loch over #69 Coomb★2|Wold, because the Coomb has nowhere to go.
//   • shire — 7×7, and A DOUBLET WHOSE TWO CELLS SHARE A TERRAIN MAY BE FOUNDED ANYWHERE, touching
//     nothing. That is a different definition of a legal move, not a bigger board. 32 cells on 48
//     open means space is never scarce, so all the tension moves onto the draft, and the game
//     becomes seeding scattered colonies and growing them into each other.
//   • holt — croft's board and croft's scoring, with the sign on the game's only dial REVERSED. The
//     reveal carries two spares, and the last claimant of the round BURNS one of the two leftovers.
//     Going last stops being a penalty and becomes a veto, so taking a high number on purpose is
//     often correct — the exact inverse of croft's incentive.
//
// The three cannot collapse into each other: croft↔shire differ on the board, the fill economics,
// the end bonus AND what a legal move is; croft↔holt share every scoring rule and play with opposite
// tempo incentives, which tests/balance.test.ts measures as an OPPOSITE-SIGNED tempo-blind delta.
//
// The HOST's pick travels frozen inside rematch.ts's round start, and every board is derived from
// (mode, seed, players) alone, so a mode can never desync. modeOf() guards an id off the wire with
// Object.hasOwn — a bare `MODES[id] ||` lets 'constructor' through as a Mode of undefined fields.

export interface Mode {
  id: string;
  name: string;
  /** Side of the square demesne. Odd, so the Motte pins the exact centre. */
  side: number;
  /** Doublets each player claims and places over the whole match. */
  perPlayer: number;
  /** Reveal carries this many more doublets than there are claims (before any burn). */
  spare: number;
  /** The reveal grows by 2 for the last third of the rounds. */
  growth: boolean;
  /** A same-terrain doublet may be founded anywhere in the frame, touching nothing. */
  colony: boolean;
  /**
   * Whoever holds the LAST claim of the round opens it by burning one doublet out of the row —
   * before anybody claims. That is what makes going last a power rather than a punishment, and it
   * had to be the whole row rather than the leftovers: burning a leftover destroys the doublet
   * nobody wanted, which the balance sim measured as worth NOTHING (see the build log).
   */
  burn: boolean;
  /** max(0, 10 − 2 × empty cells). Graded, never a cliff. */
  wholefold: boolean;
  /** +3 per region of six cells or more. */
  holdfast: boolean;
  blurb: string;
  /** One line for the how-to-play card, which has to state what differs per mode. */
  rule: string;
}

export const MODES: Record<string, Mode> = {
  croft: {
    id: 'croft',
    name: 'Croft',
    side: 5,
    perPlayer: 12,
    spare: 0,
    growth: true,
    colony: false,
    burn: false,
    wholefold: true,
    holdfast: false,
    blurb:
      'Five by five, and twelve doublets for exactly twenty-four cells — a perfect fill is possible, and it is worth ten. From the last third of the match the row widens by two, because by then it is space and not greed that is stopping you.',
    rule: 'Fill all 24 cells for the full Wholefold bonus.',
  },
  /**
   * EIGHTEEN DOUBLETS, NOT SIXTEEN, AND THE NUMBER WAS MEASURED. At sixteen (32 cells on 48 open)
   * there is so much room that every doublet is placeable everywhere, so the draft collapses to
   * "take the most coronets" and the game's central mechanic stops working: the tempo-blind control
   * arm scored −0.04 against the full bot, i.e. paying for pick order was worth NOTHING, and the
   * player holding the first slot finished 3.4 points ahead. At eighteen (36 cells on 48, 75% fill)
   * space is contested, the GAP is +2.02 ± 0.84 and the first-pick edge falls to 1.6. Turning the
   * colony rule off was the obvious fix and it was the WRONG one — it barely moved the GAP (+0.74)
   * and made the pick-order bias worse. Density was the variable the whole time.
   */
  shire: {
    id: 'shire',
    name: 'Shire',
    side: 7,
    perPlayer: 18,
    spare: 0,
    growth: false,
    colony: true,
    burn: false,
    wholefold: false,
    holdfast: true,
    blurb:
      'Seven by seven, eighteen doublets, and a doublet with the same land on both halves may be founded anywhere at all — touching nothing. Scatter colonies across open ground, then grow them into one another before the room runs out. Six cells in one region pays three. About half again as long as Croft.',
    rule: 'A matched doublet founds a colony anywhere. No Wholefold; +3 per region of 6+.',
  },
  holt: {
    id: 'holt',
    name: 'Holt',
    side: 5,
    perPlayer: 12,
    spare: 2,
    growth: false,
    colony: false,
    burn: true,
    wholefold: true,
    holdfast: false,
    blurb:
      'Croft’s board and Croft’s scoring, with the incentive turned inside out. Every row is two doublets wide of the table, and whoever claims LAST opens the row by burning one out of the match entirely — before anyone picks. Going last is a veto, so sometimes you want the expensive tile precisely because of what it lets you destroy next time.',
    rule: 'Last to claim burns one doublet from the row first. The leftover carries over.',
  },
};

export const MODE_IDS: string[] = ['croft', 'shire', 'holt'];

/**
 * Croft is the default because it is the mode that teaches the game: the reveal is exactly as wide
 * as the number of claims, so the last player really is forced and the price of greed is visible on
 * the first round rather than inferred from a rules card.
 */
export const DEFAULT_MODE = 'croft';

export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}

/**
 * Claims per player per round.
 *
 * TWO AT TWO PLAYERS, and this is load-bearing rather than a nicety. With one claim each the reveal
 * is two doublets wide and the trailing player's "choice" is forced every single round, so the
 * mechanic evaporates entirely. With two, the 2P game is the deepest on offer: you control two of
 * the four numbers and can deliberately engineer consecutive slots.
 */
export const claimsPer = (players: number): number => (players === 2 ? 2 : 1);

/** Rounds in the match. Every player claims and places `perPlayer` doublets, whatever the count. */
export const roundsIn = (mode: Mode, players: number): number => mode.perPlayer / claimsPer(players);

/** Claims made in one round, across the whole table. Also the length of the pick order. */
export const claimsPerRound = (players: number): number => players * claimsPer(players);

/** Does round `round` open with a burn? Never on the final round, where it could change nothing. */
export const burnsIn = (mode: Mode, players: number, round: number): boolean =>
  mode.burn && round < roundsIn(mode, players);

/** Turns in round `round`: the claims, plus the opening burn where there is one. */
export const slotsInRound = (mode: Mode, players: number, round: number): number =>
  claimsPerRound(players) + (burnsIn(mode, players, round) ? 1 : 0);

/** The first round of the last third, from which croft's reveal widens by two. */
export const growthFrom = (mode: Mode, players: number): number => {
  const r = roundsIn(mode, players);
  return r - Math.max(2, Math.round(r / 3)) + 1;
};

/** How many doublets are face up in round `round` (1-based). */
export function revealSize(mode: Mode, players: number, round: number): number {
  const base = claimsPerRound(players) + mode.spare;
  if (!mode.growth) return base;
  return round >= growthFrom(mode, players) ? base + 2 : base;
}

/**
 * Doublets the match will ever reveal — the size of the stratified pool.
 *
 * In holt one leftover carries into the next reveal at its own number, so only `size − 1` fresh
 * doublets are drawn from round 2 on. Never hardcode this per (mode × players): it is arithmetic,
 * and tests/rules.test.ts asserts it stays inside the 72-doublet deck for all nine combinations.
 */
export function poolSize(mode: Mode, players: number): number {
  const rounds = roundsIn(mode, players);
  let n = 0;
  for (let r = 1; r <= rounds; r++) {
    const size = revealSize(mode, players, r);
    n += r === 1 || !mode.burn ? size : size - 1;
  }
  return n;
}

/** Placeable cells: the whole frame minus the Motte at its centre. */
export const openCells = (mode: Mode): number => mode.side * mode.side - 1;
